// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Trash2, Plus, Upload, Users, Pencil, Check, X, ChevronLeft, ChevronRight, Shield, ImagePlus } from 'lucide-react';
import RLHoopsCsvImport from './RLHoopsCsvImport';
import RLHoopsTeamsManager from './RLHoopsTeamsManager';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { getSessionUser } from '@/lib/authSession';
import {
  getScoringConfigs,
  calculatePoints,
  addManualPoints,
  deletePointEntry,
} from '@/services/rlhoopsService';

const AGE_GROUPS = ['17u', '16u', '15u', '14u', '13u', '12u', '11u'];
const EVENT_TYPES = [
  { value: 'non_live', label: 'Non-Live' },
  { value: 'may_live', label: 'May Live Period' },
];

const HS_POOL_TIERS = ['A-C', 'D-F', 'G+'];
const YOUTH_POOL_TIERS = ['A-B', 'C-D', 'E+'];
const DIVISION_TIERS = ['premiere', 'showcase', 'elite', 'select'];

const PAGE_SIZE = 25;

function getCategoryForAge(ageGroup: string): 'high_school' | 'youth' {
  return ['14u', '13u', '12u', '11u'].includes(ageGroup) ? 'youth' : 'high_school';
}

export default function RLHoopsPointEntry() {
  // Form state
  const [ageGroup, setAgeGroup] = useState('');
  const [eventType, setEventType] = useState('');
  const [poolTier, setPoolTier] = useState('');
  const [divisionTier, setDivisionTier] = useState('');
  const [teamSearch, setTeamSearch] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<{ id: string; name: string } | null>(null);
  const [result, setResult] = useState<'win' | 'loss'>('win');
  const [pointDiff, setPointDiff] = useState(0);
  const [isShoeTeam, setIsShoeTeam] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Data state
  const [configs, setConfigs] = useState<any[]>([]);
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);
  const [allEntries, setAllEntries] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);

  // Entries filter/pagination
  const [filterAgeGroup, setFilterAgeGroup] = useState('all');
  const [filterType, setFilterType] = useState<'all' | 'manual' | 'csv'>('all');
  const [filterTeamSearch, setFilterTeamSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  // Inline editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ points_earned: number; result: string; point_differential: number }>({ points_earned: 0, result: 'win', point_differential: 0 });

  // Logo uploading
  const [uploadingLogoFor, setUploadingLogoFor] = useState<string | null>(null);

  // Team logo cache (team_name -> logo_url)
  const [teamLogos, setTeamLogos] = useState<Record<string, string | null>>({});

  const loadTeamLogos = useCallback(async () => {
    const { data } = await supabase
      .from('rlhoops_team_rankings')
      .select('team_name, logo_url')
      .not('logo_url', 'is', null);
    const map: Record<string, string | null> = {};
    (data || []).forEach((t: any) => {
      map[t.team_name.toLowerCase()] = t.logo_url;
    });
    setTeamLogos(map);
  }, []);

  useEffect(() => {
    loadTeamLogos();
  }, [loadTeamLogos]);

  const getLogoForTeam = (teamName: string): string | null => {
    return teamLogos[teamName?.toLowerCase()] || null;
  };

  const handleLogoUpload = async (teamName: string, file: File) => {
    setUploadingLogoFor(teamName);
    try {
      const ext = file.name.split('.').pop();
      const path = `team-logos/${teamName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('rlhoops-assets')
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('rlhoops-assets')
        .getPublicUrl(path);

      await supabase
        .from('rlhoops_team_rankings')
        .update({ logo_url: urlData.publicUrl })
        .ilike('team_name', teamName);

      toast.success(`Logo updated for ${teamName}`);
      loadTeamLogos();
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload logo');
    } finally {
      setUploadingLogoFor(null);
    }
  };

  // Load scoring configs when filters change
  useEffect(() => {
    if (ageGroup && eventType) {
      const category = getCategoryForAge(ageGroup);
      getScoringConfigs({ age_group: ageGroup, event_type: eventType, category })
        .then(setConfigs)
        .catch(() => toast.error('Failed to load scoring configs'));
    } else {
      setConfigs([]);
    }
    setPoolTier('');
    setDivisionTier('');
  }, [ageGroup, eventType]);

  // Load all entries with filters and pagination
  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      let query = supabase
        .from('rlhoops_ranking_points')
        .select('*, rlhoops_scoring_configs(config_name)', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE - 1);

      if (filterAgeGroup !== 'all') query = query.eq('age_group', filterAgeGroup);
      if (filterType === 'manual') query = query.eq('is_manual_entry', true);
      if (filterType === 'csv') query = query.eq('is_manual_entry', false);
      if (filterTeamSearch.trim()) query = query.ilike('team_name', `%${filterTeamSearch.trim()}%`);

      const { data, error, count } = await query;
      if (error) throw error;
      setAllEntries(data || []);
      setTotalCount(count || 0);
    } catch {
      toast.error('Failed to load entries');
    } finally {
      setLoadingEntries(false);
    }
  }, [currentPage, filterAgeGroup, filterType, filterTeamSearch]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [filterAgeGroup, filterType, filterTeamSearch]);

  // Search teams from tournament_brackets
  useEffect(() => {
    if (teamSearch.length < 2) {
      setTeams([]);
      return;
    }
    const timeout = setTimeout(async () => {
      const { data } = await supabase
        .from('tournament_brackets')
        .select('id, team_name')
        .ilike('team_name', `%${teamSearch}%`)
        .limit(10);
      setTeams((data || []).map(t => ({ id: t.id, name: t.team_name || '' })));
    }, 300);
    return () => clearTimeout(timeout);
  }, [teamSearch]);

  // Find matching config
  const matchedConfig = useMemo(() => {
    if (!configs.length) return null;
    const tier = eventType === 'non_live' ? poolTier : divisionTier;
    const tierField = eventType === 'non_live' ? 'pool_tier' : 'division_tier';
    if (!tier) return null;
    return configs.find(c => c[tierField] === tier) || null;
  }, [configs, eventType, poolTier, divisionTier]);

  const availableDivisionTiers = useMemo(() => {
    if (eventType !== 'may_live') return [];
    return DIVISION_TIERS.filter(tier => configs.some(c => c.division_tier === tier));
  }, [configs, eventType]);

  const calculatedPoints = useMemo(() => {
    if (!matchedConfig) return null;
    return calculatePoints(matchedConfig, result, pointDiff, isShoeTeam);
  }, [matchedConfig, result, pointDiff, isShoeTeam]);

  const showShoeTeamCheckbox = matchedConfig?.shoe_team_loss_points != null;
  const category = ageGroup ? getCategoryForAge(ageGroup) : null;
  const poolTierOptions = category === 'youth' ? YOUTH_POOL_TIERS : HS_POOL_TIERS;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleSubmit = async () => {
    if (!selectedTeam || !matchedConfig) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const { data: { user } } = await getSessionUser();
      await addManualPoints({
        team_id: selectedTeam.id,
        team_name: selectedTeam.name,
        points_earned: calculatedPoints ?? 0,
        result,
        point_differential: pointDiff,
        opponent_is_shoe_team: isShoeTeam,
        scoring_config_id: matchedConfig.id,
        age_group: ageGroup,
        entered_by: user?.id,
      });
      toast.success(`${calculatedPoints} points added for ${selectedTeam.name}`);
      setSelectedTeam(null);
      setTeamSearch('');
      setPointDiff(0);
      setIsShoeTeam(false);
      setResult('win');
      loadEntries();
    } catch (err: any) {
      toast.error(err.message || 'Failed to add points');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deletePointEntry(id);
      toast.success('Entry deleted');
      loadEntries();
    } catch {
      toast.error('Failed to delete entry');
    }
  };

  const handleSelectTeam = (team: { id: string; name: string }) => {
    setSelectedTeam(team);
    setTeamSearch(team.name);
    setShowTeamDropdown(false);
  };

  const handleUseCustomTeam = () => {
    if (teamSearch.trim()) {
      setSelectedTeam({ id: crypto.randomUUID(), name: teamSearch.trim() });
      setShowTeamDropdown(false);
    }
  };

  const startEdit = (entry: any) => {
    setEditingId(entry.id);
    setEditValues({
      points_earned: entry.points_earned,
      result: entry.result,
      point_differential: entry.point_differential,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (entryId: string) => {
    try {
      const { error } = await supabase
        .from('rlhoops_ranking_points')
        .update({
          points_earned: editValues.points_earned,
          result: editValues.result,
          point_differential: editValues.point_differential,
        })
        .eq('id', entryId);
      if (error) throw error;

      // Get entry details for recalculation
      const entry = allEntries.find(e => e.id === entryId);
      if (entry) {
        // Trigger recalculation by re-fetching
        const { data: entries } = await supabase
          .from('rlhoops_ranking_points')
          .select('points_earned, result')
          .eq('team_id', entry.team_id)
          .eq('age_group', entry.age_group)
          .eq('season', entry.season);

        const totalPoints = (entries || []).reduce((sum: number, e: any) => sum + Number(e.points_earned), 0);
        const totalWins = (entries || []).filter((e: any) => e.result === 'win').length;
        const totalLosses = (entries || []).filter((e: any) => e.result === 'loss').length;

        await supabase
          .from('rlhoops_team_rankings')
          .upsert({
            team_id: entry.team_id,
            team_name: entry.team_name,
            season: entry.season,
            age_group: entry.age_group,
            category: getCategoryForAge(entry.age_group),
            total_points: totalPoints,
            total_wins: totalWins,
            total_losses: totalLosses,
            games_played: (entries || []).length,
            last_updated: new Date().toISOString(),
          }, { onConflict: 'team_id,season,age_group' });
      }

      toast.success('Entry updated');
      setEditingId(null);
      loadEntries();
    } catch {
      toast.error('Failed to update entry');
    }
  };

  return (
    <Tabs defaultValue="manual" className="space-y-6">
      <TabsList>
        <TabsTrigger value="manual" className="gap-2"><Plus className="h-4 w-4" /> Manual Entry</TabsTrigger>
        <TabsTrigger value="csv" className="gap-2"><Upload className="h-4 w-4" /> CSV Import</TabsTrigger>
        <TabsTrigger value="entries" className="gap-2"><Users className="h-4 w-4" /> All Entries ({totalCount})</TabsTrigger>
        <TabsTrigger value="teams" className="gap-2"><Users className="h-4 w-4" /> Teams</TabsTrigger>
      </TabsList>

      <TabsContent value="manual" className="space-y-6">
        {/* Point Entry Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Manual Point Entry
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Age Group</Label>
                <Select value={ageGroup} onValueChange={setAgeGroup}>
                  <SelectTrigger><SelectValue placeholder="Select age group" /></SelectTrigger>
                  <SelectContent>
                    {AGE_GROUPS.map(ag => (
                      <SelectItem key={ag} value={ag}>{ag.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Event Type</Label>
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger><SelectValue placeholder="Select event type" /></SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map(et => (
                      <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {eventType === 'non_live' && ageGroup && (
                <div className="space-y-2">
                  <Label>Pool Tier</Label>
                  <Select value={poolTier} onValueChange={setPoolTier}>
                    <SelectTrigger><SelectValue placeholder="Select pool tier" /></SelectTrigger>
                    <SelectContent>
                      {poolTierOptions.map(pt => (
                        <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {eventType === 'may_live' && ageGroup && (
                <div className="space-y-2">
                  <Label>Division Tier</Label>
                  <Select value={divisionTier} onValueChange={setDivisionTier}>
                    <SelectTrigger><SelectValue placeholder="Select division tier" /></SelectTrigger>
                    <SelectContent>
                      {availableDivisionTiers.length > 0 ? availableDivisionTiers.map(dt => (
                        <SelectItem key={dt} value={dt}>{dt.charAt(0).toUpperCase() + dt.slice(1)}</SelectItem>
                      )) : (
                        <SelectItem value="_none" disabled>No configs found</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="space-y-2 relative">
              <Label>Team</Label>
              <Input
                placeholder="Search team name..."
                value={teamSearch}
                onChange={(e) => {
                  setTeamSearch(e.target.value);
                  setSelectedTeam(null);
                  setShowTeamDropdown(true);
                }}
                onFocus={() => setShowTeamDropdown(true)}
              />
              {showTeamDropdown && teamSearch.length >= 2 && (
                <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                  {teams.map(t => (
                    <button
                      key={t.id}
                      className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
                      onClick={() => handleSelectTeam(t)}
                    >
                      {t.name}
                    </button>
                  ))}
                  {teams.length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No matches found</div>
                  )}
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-accent text-sm border-t font-medium text-primary"
                    onClick={handleUseCustomTeam}
                  >
                    + Use "{teamSearch}" as new team
                  </button>
                </div>
              )}
              {selectedTeam && (
                <p className="text-xs text-muted-foreground">Selected: {selectedTeam.name}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="space-y-2">
                <Label>Result</Label>
                <RadioGroup value={result} onValueChange={(v) => setResult(v as 'win' | 'loss')} className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="win" id="win" />
                    <Label htmlFor="win" className="cursor-pointer">Win</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="loss" id="loss" />
                    <Label htmlFor="loss" className="cursor-pointer">Loss</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>Point Differential</Label>
                <Input
                  type="number"
                  min={0}
                  value={pointDiff}
                  onChange={(e) => setPointDiff(parseInt(e.target.value) || 0)}
                />
              </div>

              {showShoeTeamCheckbox && (
                <div className="flex items-center gap-2 pb-1">
                  <Checkbox
                    id="shoeTeam"
                    checked={isShoeTeam}
                    onCheckedChange={(v) => setIsShoeTeam(!!v)}
                  />
                  <Label htmlFor="shoeTeam" className="cursor-pointer text-sm">Opponent is Shoe Team</Label>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                {matchedConfig ? (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Config: {matchedConfig.config_name}</p>
                    <p className="text-lg font-bold">
                      Points: <span className="text-primary">{calculatedPoints ?? '—'}</span>
                    </p>
                    {result === 'loss' && matchedConfig.blowout_threshold && pointDiff >= matchedConfig.blowout_threshold && (
                      <Badge variant="destructive" className="text-xs">Blowout loss — 0 pts</Badge>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Select config options to see calculated points</p>
                )}
              </div>
              <Button onClick={handleSubmit} disabled={submitting || !selectedTeam || !matchedConfig}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Submit Points
              </Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="csv">
        <RLHoopsCsvImport />
      </TabsContent>

      {/* All Entries Tab */}
      <TabsContent value="entries" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>All Point Entries</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <Select value={filterAgeGroup} onValueChange={setFilterAgeGroup}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Age Group" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ages</SelectItem>
                  {AGE_GROUPS.map(ag => (
                    <SelectItem key={ag} value={ag}>{ag.toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterType} onValueChange={(v) => setFilterType(v as any)}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="csv">CSV Import</SelectItem>
                </SelectContent>
              </Select>

              <Input
                placeholder="Search team..."
                value={filterTeamSearch}
                onChange={(e) => setFilterTeamSearch(e.target.value)}
                className="w-[200px]"
              />
            </div>

            {loadingEntries ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : allEntries.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No entries found</p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Logo</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead>Age</TableHead>
                      <TableHead>Config</TableHead>
                      <TableHead className="text-center">Result</TableHead>
                      <TableHead className="text-center">Diff</TableHead>
                      <TableHead className="text-center">Points</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allEntries.map(entry => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          <label className="cursor-pointer block">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleLogoUpload(entry.team_name, file);
                                e.target.value = '';
                              }}
                            />
                            {uploadingLogoFor === entry.team_name ? (
                              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                            ) : getLogoForTeam(entry.team_name) ? (
                              <img
                                src={getLogoForTeam(entry.team_name)!}
                                alt={entry.team_name}
                                className="h-8 w-8 rounded-full object-cover hover:ring-2 hover:ring-primary transition-all"
                                title="Click to change logo"
                              />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center hover:ring-2 hover:ring-primary transition-all" title="Click to add logo">
                                <ImagePlus className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                          </label>
                        </TableCell>
                        <TableCell className="font-medium">{entry.team_name}</TableCell>
                        <TableCell>{entry.age_group?.toUpperCase()}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {(entry.rlhoops_scoring_configs as any)?.config_name || '—'}
                        </TableCell>

                        {editingId === entry.id ? (
                          <>
                            <TableCell className="text-center">
                              <Select value={editValues.result} onValueChange={(v) => setEditValues(prev => ({ ...prev, result: v }))}>
                                <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="win">W</SelectItem>
                                  <SelectItem value="loss">L</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-center">
                              <Input
                                type="number"
                                className="w-[60px] h-8 text-center"
                                value={editValues.point_differential}
                                onChange={(e) => setEditValues(prev => ({ ...prev, point_differential: parseInt(e.target.value) || 0 }))}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Input
                                type="number"
                                className="w-[70px] h-8 text-center"
                                value={editValues.points_earned}
                                onChange={(e) => setEditValues(prev => ({ ...prev, points_earned: parseFloat(e.target.value) || 0 }))}
                              />
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="text-center">
                              <Badge variant={entry.result === 'win' ? 'default' : 'secondary'}>
                                {entry.result === 'win' ? 'W' : 'L'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">{entry.point_differential}</TableCell>
                            <TableCell className="text-center font-bold">{entry.points_earned}</TableCell>
                          </>
                        )}

                        <TableCell>
                          <Badge variant={entry.is_manual_entry ? 'outline' : 'secondary'} className="text-xs">
                            {entry.is_manual_entry ? 'Manual' : 'CSV'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(entry.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {editingId === entry.id ? (
                              <>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => saveEdit(entry.id)}>
                                  <Check className="h-4 w-4 text-primary" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={cancelEdit}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(entry)}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(entry.id)}>
                                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <div className="flex items-center justify-between pt-2">
                  <p className="text-sm text-muted-foreground">
                    Showing {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, totalCount)} of {totalCount}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Teams Tab */}
      <TabsContent value="teams">
        <RLHoopsTeamsManager />
      </TabsContent>
    </Tabs>
  );
}
